import React, { useRef, useEffect, useState, useCallback } from 'react';

function AudioDetection({ 
  brandInfo, 
  onSoundCountChange, 
  onAudioRecorded,
  resetCount = 0,
  data,
  prevSoundCount 
}) {
  const canvasRef = useRef(null);
  const [soundCounter, setSoundCounter] = useState(0);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const analyserRef = useRef(null);
  const onSoundCountChangeRef = useRef(onSoundCountChange);
  let lastSoundDec = 0;
  useEffect(() => {
    onSoundCountChangeRef.current = onSoundCountChange;
  }, [onSoundCountChange]);

  // Add a ref to track the last recorded time
  const lastRecordedTimeRef = useRef(0);

  const startRecording = useCallback(() => {
    const currentTime = Date.now();
  
    // Check if 30 seconds have passed since last recording
    if (!data.launched) {
      return;
    }
  
    // Update the last recorded time
    lastRecordedTimeRef.current = currentTime;
  
    if (streamRef.current) {
      // Reset recorded chunks
      recordedChunksRef.current = [];
  
      // Define commonly supported audio MIME types in order of preference
      const mimeTypes = [
        'audio/mpeg',          // Very limited support for recording
        'audio/webm',         // Widely supported in Chrome, Edge, Firefox
        'audio/mp4',          // Limited support
        'audio/wav',          // Basic fallback
        'audio/ogg; codecs=opus' // Well supported in Firefox, Chrome
      ];
      
      // Try to find a supported MIME type
      let mediaRecorder;
      let supportedMimeType = null;
      
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          supportedMimeType = mimeType;
          console.log(`Using supported MIME type: ${mimeType}`);
          break;
        }
      }
      
      try {
        // Create MediaRecorder with supported MIME type if found
        if (supportedMimeType) {
          mediaRecorder = new MediaRecorder(streamRef.current, { mimeType: supportedMimeType });
        } else {
          // Use default if no specified types are supported
          mediaRecorder = new MediaRecorder(streamRef.current);
          console.warn("Using browser default audio codec");
        }
      } catch (e) {
        console.error("Error creating MediaRecorder:", e);
        // Last resort: use browser default with no options
        try {
          mediaRecorder = new MediaRecorder(streamRef.current);
          console.warn("Using browser default codec as fallback after error");
        } catch (e2) {
          console.error("Failed to initialize MediaRecorder:", e2);
          return; // Exit if we can't create a MediaRecorder
        }
      }
  
      console.log(`Recording with MIME type: ${mediaRecorder.mimeType}`);
  
      // Event handlers for recording
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
  
      mediaRecorder.onstop = () => {
        // Get the actual mime type that was used
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        
        // Create blob from recorded chunks
        const audioBlob = new Blob(recordedChunksRef.current, { 
          type: mimeType 
        });
  
        // For download functionality, could convert to MP3 here using libraries
        // but for now, we'll use the browser's recorded format
  
        // Call callback if provided
        if (onAudioRecorded) {
          onAudioRecorded(audioBlob, soundCounter);
        }
      };
  
      // Start recording
      try {
        if(typeof mediaRecorder?.start === 'function') {
          mediaRecorder.start();
        }
      } catch(err) {
        console.error('Error starting media recorder:', err);
      }
  
      // Stop recording after 3 seconds
      setTimeout(() => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
      }, 3000);
  
      mediaRecorderRef.current = mediaRecorder;
    }
   }, [data.launched, soundCounter]);

  // Rest of the component remains the same as in the original code...

  // Trigger recording when sound count changes
  useEffect(() => {
    let timeoutId;
    
    if (soundCounter > 0) {
      // Start recording
      startRecording();
  
      // Set a timeout to check soundCounter after 3 seconds
      timeoutId = setTimeout(() => {
        // console.log('Checking soundCounter after 3 seconds:', soundCounter);
      }, 3000);
    }
  
    // Cleanup function to clear timeout if component unmounts or dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [soundCounter, startRecording]);

  useEffect(() => {
    const startAudioDetection = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: true, 
          video: false 
        });
        streamRef.current = stream;

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        analyserRef.current = analyser;
        const scriptProcessor = audioContext.createScriptProcessor(1024, 1, 1);
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        analyser.connect(scriptProcessor);
        scriptProcessor.connect(audioContext.destination);

        analyser.fftSize = 2048;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const canvas = canvasRef.current;
        const canvasCtx = canvas?.getContext('2d');

        const getAverageVolume = (array) => {
          return array.reduce((sum, val) => sum + val, 0) / array.length;
        };

        let makeCount = true;
        const draw = () => {
          if (stream.active && canvas && canvasCtx && analyserRef.current) {
            // Use the ref to ensure we have a valid analyser
            analyserRef.current.getByteTimeDomainData(dataArray);

            // Clear canvas
            canvasCtx.fillStyle = "rgba(0,0,0,0)";
            canvasCtx.clearRect(0, 0, canvas.height, canvas.height);

            // Draw waveform
            canvasCtx.lineWidth = 1;
            canvasCtx.strokeStyle = brandInfo?.topbar_color || '#000';
            canvasCtx.beginPath();

            const sliceWidth = canvas.height * 1.0 / bufferLength;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
              const v = dataArray[i] / 128.0;
              const y = v * canvas.height / 2;

              if (i === 0) {
                canvasCtx.moveTo(x, y);
              } else {
                canvasCtx.lineTo(x, y);
              }
              x += sliceWidth;
            }

            canvasCtx.lineTo(canvas.width, canvas.height / 2);
            canvasCtx.stroke();
            let currentTime = new Date().getTime();

            // Sound detection
            if(currentTime - lastSoundDec > 30000){
              const average = getAverageVolume(dataArray);
              if (makeCount && average >= 127.6) {
                makeCount = false;
                setSoundCounter(prev => {
                  const newCount = prev + 1;
                  setTimeout(() => {
                    if (onSoundCountChangeRef.current) {
                      onSoundCountChangeRef.current(newCount);
                    }
                  }, 0);
                  return newCount;
              });
              lastSoundDec = currentTime
            }

              setTimeout(() => {
                makeCount = true;
              }, 100);
            }
          }

          animationFrameRef.current = requestAnimationFrame(draw);
        };

        setInterval(() => {
          draw()
        },1000)
      } catch (err) {
        console.error('Audio detection error:', err);
      }
    };

    startAudioDetection();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);


  // Reset sound counter when resetCount changes
  useEffect(() => {
    setSoundCounter(0);
  }, [resetCount]);

  return (
    <div className="audio-detect-div">
      <canvas 
        ref={canvasRef}
        width="100%" 
        height="100" 
        id="test" 
        className="audio-canvas"
      />
    </div>
  );
}

export default AudioDetection;